/**
 * Shared style constants for compact demo primitives.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type React from "react";
import { FONT } from "../../design-system/tokens.js";

export const DISPLAY_FONT = FONT.display;
export const HEADING_FONT = FONT.heading;
export const MONO_FONT = FONT.mono;
export const MATH_MONO_FONT = FONT["mono-math"];
export const OVERRIDE = "#7c3aed";
export const OVERRIDE_SOFT = "rgba(124, 58, 237, 0.1)";

export function focusRingStyle(): React.CSSProperties {
  return {
    outline: "2px solid transparent",
    outlineOffset: 2,
  };
}

export interface DemoDarkWorkbenchPalette {
  readonly accent: string;
  readonly accentSoft: string;
  readonly border: string;
  readonly monoFont: string;
  readonly muted: string;
  readonly textBright: string;
}

export interface DemoDarkWorkbenchRadii {
  readonly sm: string;
  readonly md: string;
  readonly xl: string;
}

export interface DemoDarkWorkbenchSpacing {
  readonly sm: string;
  readonly lg: string;
}

export type DemoDarkWorkbenchToneKey = "ok" | "warn" | "bad" | "info";
export type DemoDarkWorkbenchTone = Readonly<{
  background: string;
  border: string;
  soft: string;
  text: string;
}>;

export interface DemoDarkWorkbenchStyles {
  readonly accentButtonStyle: React.CSSProperties;
  readonly buttonStyle: React.CSSProperties;
  readonly cardStyle: React.CSSProperties;
  readonly inputStyle: React.CSSProperties;
  readonly monoStyle: React.CSSProperties;
  readonly sectionHeaderStyle: React.CSSProperties;
  readonly selectStyle: React.CSSProperties;
  readonly subtleCardStyle: React.CSSProperties;
  readonly TONE: Readonly<Record<DemoDarkWorkbenchToneKey, DemoDarkWorkbenchTone>>;
}

export function createDemoDarkWorkbenchStyles({
  palette,
  radii,
  spacing,
}: {
  readonly palette: DemoDarkWorkbenchPalette;
  readonly radii: DemoDarkWorkbenchRadii;
  readonly spacing: DemoDarkWorkbenchSpacing;
}): DemoDarkWorkbenchStyles {
  const buttonStyle: React.CSSProperties = {
    background: "rgba(26,35,50,0.8)",
    border: `1px solid ${palette.border}`,
    borderRadius: radii.sm,
    color: palette.textBright,
    cursor: "pointer",
    fontFamily: palette.monoFont,
    fontSize: "0.6875rem",
    padding: "0.25rem 0.625rem",
  };
  const cardStyle: React.CSSProperties = {
    background: "linear-gradient(180deg, rgba(26,35,50,0.86), rgba(17,24,39,0.78))",
    border: `1px solid ${palette.border}`,
    borderRadius: radii.xl,
    padding: spacing.lg,
  };
  const tone = (text: string, rgb: string): DemoDarkWorkbenchTone => ({
    background: `rgba(${rgb},0.08)`,
    border: `rgba(${rgb},0.4)`,
    soft: `rgba(${rgb},0.04)`,
    text,
  });
  return {
    accentButtonStyle: {
      ...buttonStyle,
      background: palette.accentSoft,
      border: `1px solid color-mix(in srgb, ${palette.accent} 30%, transparent)`,
      color: palette.accent,
    },
    buttonStyle,
    cardStyle,
    inputStyle: {
      ...buttonStyle,
      background: "rgba(10,14,23,0.6)",
      flex: 1,
      fontSize: "0.625rem",
      padding: "0.25rem 0.5rem",
    },
    monoStyle: { fontFamily: palette.monoFont },
    sectionHeaderStyle: {
      color: palette.muted,
      fontSize: "0.6875rem",
      fontWeight: 700,
      letterSpacing: 0,
      marginBottom: spacing.sm,
      textTransform: "uppercase",
    },
    selectStyle: {
      ...buttonStyle,
      borderRadius: radii.md,
      padding: "0.375rem 0.75rem",
    },
    subtleCardStyle: {
      ...cardStyle,
      background: "linear-gradient(180deg, rgba(26,35,50,0.6), rgba(17,24,39,0.5))",
    },
    TONE: {
      bad: tone("#f87171", "239,68,68"),
      info: tone("#38bdf8", "56,189,248"),
      ok: tone("#22c55e", "34,197,94"),
      warn: tone("#f59e0b", "245,158,11"),
    },
  };
}
